// Outreach holds: the owner-facing surface for "paused customers".
//
// Holds pause EVERY custom workflow's marketing sends to a contact (the
// engine checks them before each send; see workflow_model/guards.ts). Most
// are written by the AI when a conversation signals a sensitive circumstance
// ("my dad passed away…"); this panel makes each one visible, explains why
// it exists, and lets the owner clear it early or place one manually.
// Clearing sets cleared_at (never deletes) so the audit trail survives.

import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { CircleSlash, PauseCircle, Play } from "lucide-react";

interface HoldRow {
  id: string;
  contact_id: string;
  hold_until: string;
  reason: string;
  source: "ai" | "owner" | "case" | "workflow" | "archive";
  created_at: string;
}

interface ContactHit {
  contact_id: string;
  display_name: string | null;
  primary_email: string | null;
  primary_phone: string | null;
}

const contactLabel = (c: ContactHit | undefined, fallbackId: string) =>
  c?.display_name || c?.primary_email || c?.primary_phone || `Contact ${fallbackId.slice(0, 8)}`;

const fmtUntil = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });

const OutreachHolds = ({ businessId }: { businessId: string }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [busyId, setBusyId] = useState<string | null>(null);

  // Manual-pause popover state.
  const [pauseOpen, setPauseOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState<ContactHit | null>(null);
  const [days, setDays] = useState("7");
  const [reason, setReason] = useState("");
  const [placing, setPlacing] = useState(false);

  const { data: holds } = useQuery({
    queryKey: ["outreach-holds", businessId],
    queryFn: async (): Promise<HoldRow[]> => {
      const { data, error } = await (supabase as any)
        .from("contact_outreach_holds")
        .select("id, contact_id, hold_until, reason, source, created_at")
        .eq("business_id", businessId)
        .is("cleared_at", null)
        .gt("hold_until", new Date().toISOString())
        .order("hold_until", { ascending: true })
        .limit(50);
      if (error) throw new Error(error.message);
      return (data ?? []) as HoldRow[];
    },
    enabled: !!businessId,
  });

  const contactIds = useMemo(() => [...new Set((holds ?? []).map((h) => h.contact_id))], [holds]);
  const { data: contacts } = useQuery({
    queryKey: ["outreach-hold-contacts", contactIds.join(",")],
    queryFn: async (): Promise<Map<string, ContactHit>> => {
      const { data, error } = await (supabase as any)
        .from("contacts")
        .select("contact_id, display_name, primary_email, primary_phone")
        .in("contact_id", contactIds);
      if (error) throw new Error(error.message);
      return new Map(((data ?? []) as ContactHit[]).map((c) => [c.contact_id, c]));
    },
    enabled: contactIds.length > 0,
  });

  const { data: searchHits } = useQuery({
    queryKey: ["outreach-hold-search", businessId, search],
    queryFn: async (): Promise<ContactHit[]> => {
      const { data, error } = await (supabase as any)
        .from("contacts")
        .select("contact_id, display_name, primary_email, primary_phone")
        .eq("business_id", businessId)
        .ilike("display_name", `%${search.trim()}%`)
        .limit(6);
      if (error) throw new Error(error.message);
      return (data ?? []) as ContactHit[];
    },
    enabled: pauseOpen && search.trim().length >= 2 && !picked,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["outreach-holds", businessId] });

  const clearHold = async (hold: HoldRow) => {
    setBusyId(hold.id);
    try {
      const { error } = await (supabase as any)
        .from("contact_outreach_holds")
        .update({ cleared_at: new Date().toISOString(), cleared_by: user?.id ?? null })
        .eq("business_id", businessId)
        .eq("id", hold.id);
      if (error) throw new Error(error.message);
      toast.success("Outreach resumed for this customer.");
      refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Couldn't clear the hold");
    } finally {
      setBusyId(null);
    }
  };

  const placeHold = async () => {
    if (!picked) {
      toast.error("Pick a customer first.");
      return;
    }
    setPlacing(true);
    try {
      const { error } = await (supabase as any).from("contact_outreach_holds").insert({
        business_id: businessId,
        contact_id: picked.contact_id,
        hold_until: new Date(Date.now() + Number(days) * 86_400_000).toISOString(),
        reason: reason.trim() || "Paused by you",
        source: "owner",
        created_by: user?.id ?? null,
      });
      if (error) throw new Error(error.message);
      toast.success(`Workflows will leave ${contactLabel(picked, picked.contact_id)} alone for ${days} days.`);
      setPauseOpen(false);
      setPicked(null);
      setSearch("");
      setReason("");
      refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Couldn't place the hold");
    } finally {
      setPlacing(false);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="mb-3 flex items-center gap-2">
        <PauseCircle className="h-5 w-5 text-muted-foreground" />
        <h3 className="font-semibold text-foreground">Paused customers</h3>
        {(holds?.length ?? 0) > 0 && (
          <Badge variant="secondary" className="text-[10px]">{holds!.length}</Badge>
        )}
      </div>

      {(holds?.length ?? 0) === 0 ? (
        <p className="text-xs text-muted-foreground">
          Nobody is paused. When the AI spots a sensitive moment in a conversation (a bereavement, an
          emergency, a "not now"), it pauses that customer's workflow messages and the pause shows up
          here — you can end it early anytime.
        </p>
      ) : (
        <ul className="space-y-2">
          {(holds ?? []).map((h) => (
            <li key={h.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border px-3 py-2">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-foreground">
                  {contactLabel(contacts?.get(h.contact_id), h.contact_id)}
                  <span className="ml-2 align-middle">
                    <Badge variant="outline" className="text-[10px]">
                      {h.source === "ai" ? "Paused by AI" : h.source === "archive" ? "Archived" : "Paused by you"}
                    </Badge>
                  </span>
                </p>
                <p className="truncate text-xs text-muted-foreground">
                  {h.source === "archive" ? "Paused until unarchived" : `${h.reason} · until ${fmtUntil(h.hold_until)}`}
                </p>
              </div>
              {h.source === "archive" ? (
                <span className="px-2 text-xs text-muted-foreground">Unarchive from Customers</span>
              ) : <Button
                variant="ghost"
                size="sm"
                className="gap-1.5 text-muted-foreground"
                onClick={() => clearHold(h)}
                disabled={busyId === h.id}
              >
                <Play className="h-3.5 w-3.5" />
                {busyId === h.id ? "Resuming…" : "Resume"}
              </Button>}
            </li>
          ))}
        </ul>
      )}

      <Popover open={pauseOpen} onOpenChange={setPauseOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="mt-3 w-full gap-1.5">
            Pause someone manually
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-80 space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Customer</Label>
              {picked ? (
                <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
                  <span className="text-sm">{contactLabel(picked, picked.contact_id)}</span>
                  <button
                    type="button"
                    className="text-xs text-muted-foreground hover:text-foreground"
                    onClick={() => setPicked(null)}
                  >
                    change
                  </button>
                </div>
              ) : (
                <>
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by name…"
                    className="h-9"
                  />
                  {(searchHits ?? []).map((c) => (
                    <button
                      key={c.contact_id}
                      type="button"
                      onClick={() => setPicked(c)}
                      className="block w-full rounded-lg border border-border px-3 py-2 text-left text-sm hover:border-primary"
                    >
                      {contactLabel(c, c.contact_id)}
                    </button>
                  ))}
                </>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Pause for</Label>
              <Select value={days} onValueChange={setDays}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="3">3 days</SelectItem>
                  <SelectItem value="7">7 days</SelectItem>
                  <SelectItem value="14">14 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Why (shown to you later)</Label>
              <Input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. family emergency — asked for space"
                maxLength={200}
                className="h-9"
              />
            </div>
            <Button size="sm" className="w-full" onClick={placeHold} disabled={placing}>
              {placing ? "Pausing…" : "Pause outreach"}
            </Button>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default OutreachHolds;
